import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { themes, getTheme, DEFAULT_THEME, isValidTheme } from '../config/themes';
import { useAuthContext } from '../features/auth/context/AuthContext';

/**
 * Helper function to convert hex to RGB
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result 
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
};

/**
 * Theme Context
 * 🎨 Provides theme state and management throughout the app
 */
const ThemeContext = createContext();

/**
 * Custom hook to use theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 * Manages theme state and applies theme to the document
 */
export const ThemeProvider = ({ children }) => {
  const { user, updateUser } = useAuthContext();
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const [themeColors, setThemeColors] = useState(getTheme(DEFAULT_THEME));
  
  // Store applyThemeToDocument in a ref to avoid recreating it
  const applyThemeToDocumentRef = React.useRef(null);

  // Initialize theme immediately on mount (before user loads)
  useEffect(() => {
    // First, try to get theme from localStorage for immediate application
    const savedTheme = localStorage.getItem('theme');
    let initialTheme = DEFAULT_THEME;
    
    if (savedTheme && isValidTheme(savedTheme)) {
      initialTheme = savedTheme;
    }
    
    // ALWAYS apply the theme on mount. Previously this was guarded by
    // `theme !== initialTheme`, which meant the CSS variables (--bg-primary,
    // --text-primary, etc.) were NEVER set for fresh sessions using the
    // default theme (e.g. incognito / first visit), leaving every
    // var(--...)-based style unresolved (transparent backgrounds, etc.).
    console.log(`🎨 Initial theme load: ${initialTheme}`);
    applyThemeToDocument(initialTheme);
    
    if (theme !== initialTheme) {
      setThemeState(initialTheme);
      setThemeColors(getTheme(initialTheme));
    }
  }, []); // Run once on mount

  // Update theme when user data loads (user.theme takes priority)
  useEffect(() => {
    if (user?.theme && isValidTheme(user.theme)) {
      // User theme takes priority over localStorage
      if (theme !== user.theme) {
        console.log(`🎨 Theme updated from user preference: ${user.theme}`);
        setThemeState(user.theme);
        setThemeColors(getTheme(user.theme));
        applyThemeToDocument(user.theme);
        // Sync localStorage with user preference
        localStorage.setItem('theme', user.theme);
      }
    }
  }, [user?.theme]); // Only depend on user.theme to avoid infinite loops

  /**
   * Apply theme CSS variables to document root
   */
  const applyThemeToDocument = (themeName) => {
    const theme = getTheme(themeName);
    const root = document.documentElement;
    
    // Set data-theme attribute for CSS selectors
    root.setAttribute('data-theme', themeName);
    
    // Apply CSS variables
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-dark', theme.primaryDark);
    root.style.setProperty('--theme-primary-light', theme.primaryLight);
    root.style.setProperty('--theme-primary-50', theme.primary50);
    root.style.setProperty('--theme-primary-100', theme.primary100);
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-secondary-dark', theme.secondaryDark);
    root.style.setProperty('--theme-accent', theme.accent);
    
    // Background gradient - use backgroundGradient if available, fallback to background
    const gradient = theme.backgroundGradient || theme.background;
    root.style.setProperty('--bg-gradient-from', gradient.from);
    root.style.setProperty('--bg-gradient-via', gradient.via);
    root.style.setProperty('--bg-gradient-to', gradient.to);
    
    // Text colors
    root.style.setProperty('--text-primary', theme.text.primary);
    root.style.setProperty('--text-secondary', theme.text.secondary);
    root.style.setProperty('--text-light', theme.text.light);
    root.style.setProperty('--text-on-primary', theme.text.onPrimary);
    
    // Background colors
    root.style.setProperty('--bg-primary', theme.background.primary);
    root.style.setProperty('--bg-secondary', theme.background.secondary);
    root.style.setProperty('--bg-tertiary', theme.background.tertiary);
    root.style.setProperty('--bg-overlay', theme.background.overlay);
    
    // Border colors
    root.style.setProperty('--border-primary', theme.border.primary);
    root.style.setProperty('--border-secondary', theme.border.secondary);
    root.style.setProperty('--border-focus', theme.border.focus);
    
    // Interactive states
    root.style.setProperty('--hover-primary', theme.interactive.hoverPrimary);
    root.style.setProperty('--hover-bg', theme.interactive.hoverBg);
    root.style.setProperty('--active-primary', theme.interactive.activePrimary);
    root.style.setProperty('--focus-ring', theme.interactive.focusRing);
    
    // Status colors
    root.style.setProperty('--success', theme.status.success);
    root.style.setProperty('--error', theme.status.error);
    root.style.setProperty('--warning', theme.status.warning);
    root.style.setProperty('--info', theme.status.info);
    
    // Forest theme specific - warm accent colors
    if (themeName === 'forest' && theme.accentWarm) {
      root.style.setProperty('--theme-accent-warm', theme.accentWarm);
      root.style.setProperty('--theme-accent-warm-light', theme.accentWarmLight);
      root.style.setProperty('--bg-warm-accent', theme.background.warmAccent);
      root.style.setProperty('--hover-warm', theme.interactive.hoverWarm);
    } else {
      // Remove forest-specific variables for other themes
      root.style.removeProperty('--theme-accent-warm');
      root.style.removeProperty('--theme-accent-warm-light');
      root.style.removeProperty('--bg-warm-accent');
      root.style.removeProperty('--hover-warm');
    }
    
    // Update body background gradient
    const body = document.body;
    if (body) {
      // Remove all theme classes
      const currentClasses = body.className.split(' ').filter(cls => !cls.startsWith('theme-'));
      body.className = currentClasses.join(' ').trim();
      // Add new theme class
      body.classList.add(`theme-${themeName}`);
      
      // Get theme colors
      const theme = getTheme(themeName);
      // Convert hex colors to rgba format for via and to
      // Use backgroundGradient instead of background to avoid conflicts
      const gradient = theme.backgroundGradient || theme.background;
      const fromHex = gradient.from;
      const viaRgb = hexToRgb(gradient.via);
      const toRgb = hexToRgb(gradient.to);
      
      const viaRgba = viaRgb ? `rgba(${viaRgb}, 0.8)` : 'rgba(96, 165, 250, 0.8)';
      const toRgba = toRgb ? `rgba(${toRgb}, 0.7)` : 'rgba(168, 85, 247, 0.7)';
      
      // Set inline style with !important to override any other styles
      // Use background-attachment: fixed so gradient shows through all content
      body.setAttribute('style', `
        background: radial-gradient(ellipse at top right, ${fromHex} 0%, ${viaRgba} 50%, ${toRgba} 100%) !important;
        background-size: 200% 200% !important;
        background-attachment: fixed !important;
        background-color: transparent !important;
        background-repeat: no-repeat !important;
        animation: aurora 25s ease-in-out infinite !important;
      `);
      
      // Also update CSS variables for the body element
      body.style.setProperty('--bg-gradient-from', fromHex, 'important');
      body.style.setProperty('--bg-gradient-via', viaRgba, 'important');
      body.style.setProperty('--bg-gradient-to', toRgba, 'important');
    }
  };

  /**
   * Set theme and persist to user profile
   */
  const setTheme = useCallback(async (themeName) => {
    if (!isValidTheme(themeName)) {
      console.warn(`Invalid theme: ${themeName}, using default`);
      themeName = DEFAULT_THEME;
    }
    
    // Update state immediately for instant UI update
    setThemeState(themeName);
    const newThemeColors = getTheme(themeName);
    setThemeColors(newThemeColors);
    applyThemeToDocument(themeName);
    
    // Save to localStorage for immediate persistence
    localStorage.setItem('theme', themeName);
    
    // Save to user profile if authenticated
    if (user) {
      try {
        if (updateUser) {
          const response = await updateUser({ theme: themeName });
          console.log(`✅ Theme saved to user profile: ${themeName}`, response);
          // Refresh user data to get updated theme
          if (response && response.user && response.user.theme) {
            // Theme will be picked up by the useEffect when user.theme changes
          }
        }
      } catch (error) {
        console.error('❌ Failed to save theme to user profile:', error);
        // Theme is still applied locally, just not persisted
      }
    }
  }, [user, updateUser]);

  const value = {
    theme,
    themeColors,
    setTheme,
    themes, // Expose all themes for theme selector
    isDark: false, // Future: dark mode support
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
